import { Button } from "@chakra-ui/button";
import { Box, HStack, Text } from "@chakra-ui/layout";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { Input, Spinner } from "@chakra-ui/react";
import { Tag, TagLabel } from "@chakra-ui/tag";
import React, { useState } from "react";
import {
  ORGANISATION_PICKER_PAGE_SIZE,
  useSearchOrganisations,
} from "@tether-web-portal/api/organisations/hooks";
import { useDebounce } from "@tether-web-portal/hooks/useDebounce";
import { useAuth } from "../../lib/auth/useAuth";

interface SwitchOrganisationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentOrganisationId: string;
  /**
   * The current organisation's name, fetched by id. Shown above the list because the
   * current organisation need not be on the page of organisations the list holds.
   */
  currentOrganisationName?: string;
  isCovidCareHeader?: boolean;
}

/**
 * The search box appears once there are more organisations than this — the threshold the
 * switcher always had. Below it the list is short enough to read.
 */
const SEARCH_BOX_THRESHOLD = 5;

/**
 * Change organisation: one page of the user's organisations, searched on the server.
 *
 * It used to be handed every organisation the user belongs to and filter them in the
 * browser, which for Tether's support user (a member of every organisation) was thousands
 * of rows downloaded on every page view. It now asks for the first page by name when it
 * opens, and a search asks the API, so an organisation past the first page is found by
 * typing its name.
 */
export default function SwitchOrganisationModal({
  isOpen,
  currentOrganisationId,
  currentOrganisationName,
  onClose,
  isCovidCareHeader,
}: SwitchOrganisationModalProps) {
  const [isSubmittingOrgId, setIsSubmitting] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState("");
  const search = useDebounce(searchText.trim(), 300);
  const { switchOrganisation } = useAuth();

  const { items, total, isLoading, isError, isFetching, isPreviousData, refetch } = useSearchOrganisations(
    { search, limit: ORGANISATION_PICKER_PAGE_SIZE, offset: 0 },
    // Nothing is asked for until the switcher is opened.
    { enabled: isOpen }
  );

  const handleSwitchOrganisation = async (organisationId: string) => {
    setIsSubmitting(organisationId);
    try {
      const res = await switchOrganisation(organisationId);
      setIsSubmitting(undefined);
      if (res) {
        if (isCovidCareHeader) {
          window.location.href = `/ext/airborne-index/${organisationId}`;
        } else {
          window.location.href = "/";
        }
      }
    } catch (e) {
      console.log("unable to switch to new org");
    }
  };

  // The rows on screen are not yet the answer to what is in the box: typed and not yet
  // settled, or settled and still showing the previous search's rows while it loads. They
  // are dimmed, and an empty answer is never read as "no matches" until it is the latest.
  const isSettling = searchText.trim() !== search || (isPreviousData && isFetching);
  const showSearchBox = searchText !== "" || search !== "" || total > SEARCH_BOX_THRESHOLD;

  const renderList = () => {
    if (isError) {
      return (
        <Box my={6} role="alert">
          <Text mb={3}>Your organisations could not be loaded.</Text>
          <Button size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <HStack my={6} justifyContent="center">
          <Spinner size="sm" label="Loading organisations" />
        </HStack>
      );
    }

    if (items.length === 0) {
      if (isSettling) return null;
      return (
        <Text my={6} color="gray.600">
          {search ? `No organisations match "${search}".` : "No organisations to show."}
        </Text>
      );
    }

    return (
      <Box opacity={isSettling ? 0.5 : 1} aria-busy={isSettling}>
        {items.map((o) => (
          <HStack key={o.id} my={6} justifyContent="space-between">
            <Text>{o.name}</Text>

            {Boolean(o.id === currentOrganisationId) && (
              <Tag size="md" colorScheme="black" borderRadius="full">
                <TagLabel>Current Organisation</TagLabel>
              </Tag>
            )}

            {Boolean(o.id !== currentOrganisationId) && (
              <Button isLoading={isSubmittingOrgId === o.id} onClick={() => handleSwitchOrganisation(o.id)}>
                Switch
              </Button>
            )}
          </HStack>
        ))}
        {total > items.length && (
          <Text fontSize="sm" color="gray.600">
            Showing {items.length} of {total.toLocaleString()} — search to find the rest.
          </Text>
        )}
      </Box>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Organisation</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {currentOrganisationName && (
              <Text mb={4} fontSize="sm" color="gray.600">
                Current organisation: <b>{currentOrganisationName}</b>
              </Text>
            )}
            {showSearchBox && (
              <Input
                type="search"
                placeholder="Search..."
                aria-label="Search organisations"
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
                autoFocus
              />
            )}
            {renderList()}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
